import type { SupportedLocale } from '@fleetcore/shared'
import type {
  CapabilityReason,
  CapabilityStatus,
  ChannelAvailability,
  ChannelCategory,
  ChannelKey,
} from '@fleetcore/shared/telemetry'

import type { CapabilityProfile, ChannelCapabilityView } from './api-types'

/**
 * `GET /vehicles/:id/capabilities` as the API sends it today
 * (`VehicleCapabilityProfileView` in `apps/api/src/capabilities/capabilities.service.ts`).
 *
 * The endpoint moved out of the telemetry module and changed shape: no top-level
 * `reasonCode`, no `lookbackDays`, `byAvailability` instead of `availability`. The
 * page was still reading the old fields, so `reasonCode` arrived `undefined`, the
 * notice looked up an icon for it, got `undefined`, and React refused to render the
 * whole vehicle page (minified error #130). This adapter is the one place the two
 * shapes meet.
 */
interface ChannelCapabilityWire {
  readonly channel: string
  readonly availability: ChannelAvailability | null
  readonly status: CapabilityStatus
  readonly lastSeenAt: string | null
  readonly samples: number | null
  readonly observedInWindow: boolean
  readonly avlId: number | null
}

export interface VehicleCapabilityProfileWire {
  readonly vehicleId: string
  readonly locale: SupportedLocale
  readonly windowDays: number
  readonly staleAfterHours: number
  readonly programNumber: number | null
  readonly canBus: {
    readonly status: CapabilityStatus
    readonly reason: CapabilityReason
    readonly lastSeenAt: string | null
    readonly reportingChannels: number
    readonly stoppedChannels: number
    readonly knownChannels: number
    readonly programNumber: number | null
  }
  readonly categories: readonly {
    readonly category: ChannelCategory
    readonly reporting: number
    readonly channels: readonly ChannelCapabilityWire[]
  }[]
  readonly byAvailability: readonly {
    readonly availability: ChannelAvailability
    readonly knownChannels: number
    readonly reporting: number
  }[]
  readonly unmappedChannels: readonly ChannelCapabilityWire[]
  readonly observedChannels: number
  readonly lastObservedAt: string | null
}

/** The page's shape already (an older API, or a test fixture): nothing to do. */
function isPageShape(body: unknown): body is CapabilityProfile {
  return typeof body === 'object' && body !== null && 'reasonCode' in body && 'availability' in body
}

export function toCapabilityProfile(body: unknown): CapabilityProfile {
  if (isPageShape(body)) return body
  const wire = body as VehicleCapabilityProfileWire

  const channels: ChannelCapabilityView[] = (wire.categories ?? []).flatMap((category) =>
    category.channels.map(channelView),
  )
  const canReporting = wire.canBus?.status === 'reporting'

  return {
    vehicleId: wire.vehicleId,
    locale: wire.locale,
    lookbackDays: wire.windowDays,
    observedChannels: wire.observedChannels ?? 0,
    lastObservedAt: wire.lastObservedAt ?? null,
    categories: (wire.categories ?? []).map((category) => {
      const reporting = category.channels.filter((c) => c.status === 'reporting')
      return {
        category: category.category,
        reported: reporting.length > 0,
        channelCount: reporting.length,
        channels: reporting.map((c) => c.channel as ChannelKey),
      }
    }),
    availability: (wire.byAvailability ?? []).map((row) => ({
      availability: row.availability,
      knownChannels: row.knownChannels,
      reportedChannels: row.reporting,
      reported: row.reporting > 0,
    })),
    canBus: {
      available: canReporting,
      reportedChannels: wire.canBus?.reportingChannels ?? 0,
      programNumber: wire.canBus?.programNumber ?? wire.programNumber ?? null,
      reasonCode: canReporting ? null : 'can_bus_not_reporting',
      status: wire.canBus?.status,
      reason: wire.canBus?.reason,
      lastSeenAt: wire.canBus?.lastSeenAt ?? null,
      stoppedChannels: wire.canBus?.stoppedChannels ?? 0,
      knownChannels: wire.canBus?.knownChannels ?? 0,
    },
    unmapped: (wire.unmappedChannels ?? []).map((c) => ({
      channel: c.channel as ChannelKey,
      avlId: c.avlId,
      samples: c.samples ?? 0,
    })),
    reasonCode:
      wire.lastObservedAt === null
        ? 'no_telemetry_in_window'
        : canReporting
          ? null
          : 'can_bus_not_reporting',
    channels,
    staleAfterHours: wire.staleAfterHours,
  }
}

function channelView(wire: ChannelCapabilityWire): ChannelCapabilityView {
  return {
    channel: wire.channel as ChannelKey,
    availability: wire.availability,
    status: wire.status,
    lastSeenAt: wire.lastSeenAt,
    samples: wire.samples,
    observedInWindow: wire.observedInWindow,
  }
}
